import { InstructorDetail } from "@features/instructor/types";
import { separateMoneyNumbers, toFarsiNumber } from "@utils/digits";
import Graduated from "@svg/new-design/graduated";
import LightOutline3User from "@svg/new-design/light-outline-3-user";
import BookIcon from "@svg/new-design/book";
import ClockIcon from "@svg/new-design/clock";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import newExploreApi from "@api/new-explore";
import {
  exploreMethodBySlug,
  NewExploreCoursesResponse,
  NewExplorePayload,
} from "types/api/new-explore";
import { RequestMeta } from "@types";
import { useRouter } from "next/router";
import { EXPLORE_SORT } from "enums/new-explore/explore-sort";

import { useRef } from "react";
import { InstructorPaginationLimit } from "../helpers/constants";

export const useInstructor = (instructor: InstructorDetail) => {
  const instructorBg = useRef<HTMLDivElement>(null);
  const { query } = useRouter();
  const { data, error, isLoading } = useRequestImmutable(
    (
      newExploreApi[exploreMethodBySlug.course] as (
        params?: NewExplorePayload
      ) => RequestMeta<NewExploreCoursesResponse, never, NewExplorePayload>
    )({
      page: Number(query.page) || 1,
      sort: (query.sort as EXPLORE_SORT) || EXPLORE_SORT.MOST_SOLD,
      count: InstructorPaginationLimit,
      instructor_id: String(instructor.id),
      bypass_exclusion: 1,
    })
  );
  const checkInstructorDegree =
    instructor.instructor_degree !== "word.instructor_degree." &&
    instructor.instructor_degree &&
    instructor.instructor_degree !== "word.profile_degree.";

  const checkInstructorGrade = instructor.instructor_grade !== null;

  const DegreeGrade = () => {
    if (checkInstructorDegree && checkInstructorGrade)
      return `${instructor.instructor_degree} ${instructor.instructor_grade}`;
    if (checkInstructorDegree) return instructor.instructor_degree;
    if (checkInstructorGrade) return instructor.instructor_grade;
  };

  const prepareNumber = (value: number) =>
    separateMoneyNumbers(toFarsiNumber(Math.round(value)));

  const multiplier = {
    میلیون: 1000000,
    هزار: 1000,
    صد: 100,
  };

  const showInstructorStatus =
    instructor.total_students ||
    Math.round(
      (instructor.total_study_time *
        (multiplier[instructor.total_study_time_multiplier] || 1)) /
        60
    ) ||
    instructor.total_duration;

  const instructorStatistic = [
    {
      name: "نفر دانشجو",
      icon: Graduated,

      value: prepareNumber(instructor.total_students),
      sort: 1,
      enable: !!instructor.total_students,
    },
    {
      name: "ساعت یادگیری",
      icon: LightOutline3User,

      value: prepareNumber(
        Math.round(
          (instructor.total_study_time *
            (multiplier[instructor.total_study_time_multiplier] || 1)) /
            60
        )
      ),
      sort: 3,
      enable: !!instructor.total_study_time,
    },
    {
      name: "عنوان آموزش",
      icon: BookIcon,
      value: prepareNumber(instructor.total_count),
      sort: 4,
      enable: !!instructor.total_count,
    },
    {
      name: `${instructor.total_duration / 60 >= 1 ? "ساعت" : "دقیقه"} آموزش`,
      icon: ClockIcon,
      value: prepareNumber(
        Math.round(
          instructor.total_duration / 60 >= 1
            ? instructor.total_duration / 60
            : instructor.total_duration
        )
      ),
      sort: 2,
      enable: !!instructor.total_duration,
    },
  ];

  return {
    instructorStatistic,
    showInstructorStatus,
    DegreeGrade,
    data,
    error,
    isLoading,
    instructorBg,
  };
};
